import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import {
  FormErrorSummary,
  notifyInvalidSubmit,
} from "~/components/form/form-error-summary";
import {
  FormAdvancedCheckboxGroupField,
  FormCheckboxField,
  FormCheckboxGroupField,
  FormInputField,
  FormRadioGroupField,
  FormSelectField,
  FormSingleCheckboxField,
} from "~/components/form/form-fields";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { useCoachEmbeddingUpdater } from "~/hooks/use-coach-embedding-updater";
import {
  getCoachDetailsData,
  getExpertiseOptions,
  saveCoachDetails,
} from "~/lib/functions/coach-details";
import {
  CoachDetailsSchema,
  type CoachDetailsFormData,
} from "~/lib/shared/schemas/coach-details";

type FormData = CoachDetailsFormData;

export const Route = createFileRoute("/_user/my-coaching/profile/details")({
  staticData: { title: "Matching details" },
  loader: async () => {
    const [coachData, expertiseOptions] = await Promise.all([
      getCoachDetailsData(),
      getExpertiseOptions(),
    ]);
    return { coachData, expertiseOptions };
  },
  component: DetailsPage,
});

function DetailsPage() {
  const queryClient = useQueryClient();
  const { coachData, expertiseOptions } = Route.useLoaderData();
  const { refresh: refreshCoachEmbedding } = useCoachEmbeddingUpdater();

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    reset,
    control,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(CoachDetailsSchema),
    defaultValues: coachData,
  });

  const { isDirty, errors } = formState;

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => saveCoachDetails({ data }),
    onSuccess: async (freshData) => {
      toast.success("Coach details saved");
      reset(freshData);
      await refreshCoachEmbedding();
      queryClient.invalidateQueries({ queryKey: ["coach"] });
    },
    onError: () => {
      toast.error("Failed to save coach details");
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => isDirty,
  );

  const watchedPrimaryExpertise = watch("primaryExpertise");
  const watchedSecondaryExpertise = watch("secondaryExpertise");

  React.useEffect(() => {
    const secondaryValues = getValues("secondaryExpertise") || [];
    const newSecondaryValues = secondaryValues.filter(
      (item) => !watchedPrimaryExpertise.includes(item),
    );
    if (newSecondaryValues.length !== secondaryValues.length) {
      setValue("secondaryExpertise", newSecondaryValues, { shouldDirty: true });
    }
  }, [watchedPrimaryExpertise, getValues, setValue]);

  React.useEffect(() => {
    const primaryValues = getValues("primaryExpertise") || [];
    const newPrimaryValues = primaryValues.filter(
      (item) => !watchedSecondaryExpertise.includes(item),
    );
    if (newPrimaryValues.length !== primaryValues.length) {
      setValue("primaryExpertise", newPrimaryValues, { shouldDirty: true });
    }
  }, [watchedSecondaryExpertise, getValues, setValue]);

  const handleFaithIntegrationChange = (
    values: string[],
    selectedValue: string,
  ) => {
    if (
      selectedValue === "I do not integrate spirituality into my coaching" &&
      values.includes(selectedValue)
    ) {
      return ["I do not integrate spirituality into my coaching"];
    }
    return values.filter(
      (v) => v !== "I do not integrate spirituality into my coaching",
    );
  };

  const expertiseByCategory = React.useMemo(() => {
    const map = new Map<string, typeof expertiseOptions>();
    expertiseOptions.forEach((option) => {
      if (!option.categoryName) return;
      if (!map.has(option.categoryName)) {
        map.set(option.categoryName, []);
      }
      map.get(option.categoryName)?.push(option);
    });
    return map;
  }, [expertiseOptions]);

  return (
    <>
      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
      <form
        onSubmit={handleSubmit(onSubmit, notifyInvalidSubmit)}
        className="space-y-8"
      >
        <ProfileEditHeader
          titleAs="h2"
          title="Matching details"
          description="Private details that will help our algorithm match you with the right clients."
          username={coachData.username}
          form={formState}
          isSaving={saveMutation.isPending}
        />

        <FormErrorSummary
          errors={errors}
          labels={{
            coachingFormats: "Coaching format",
            sessionDeliveryMethods: "Session delivery method",
            primaryExpertise: "Primary expertise",
            secondaryExpertise: "Secondary expertise",
            coachingApproaches: "Coaching style & approach",
            faithIntegration: "Faith & spirituality",
            experienceLevel: "Experience level",
            dateOfBirth: "Date of birth",
            discType: "DISC type",
            mbtiType: "Myers-Briggs (MBTI) type",
            enneagramType: "Enneagram type",
            humanDesignType: "Human Design type",
          }}
        />

        <Card>
          <CardHeader>
            <CardTitle>Demographics</CardTitle>
            <CardDescription>
              Sharing demographic information is optional. It enhances the
              matching process by helping clients find a coach that aligns with
              their preferences.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input {...register("coachId")} type="hidden" />
            <input {...register("id")} type="hidden" />
            <input {...register("username")} type="hidden" />

            <FormInputField
              control={control}
              name="dateOfBirth"
              label="Date of birth"
              helpText="Your date of birth"
              type="date"
              isPrivate
            />

            <FormSelectField
              control={control}
              name="gender"
              label="Gender"
              helpText="Your gender"
              options={[
                "Woman",
                "Man",
                "Non-Binary",
                "Prefer to self-describe",
                "Prefer not to say",
              ]}
              placeholder="Select gender"
              isPrivate
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching focus & expertise</CardTitle>
            <CardDescription>
              What areas do you specialize in? These selections determine which
              client goals you'll be matched with.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {Array.from(expertiseByCategory.entries()).map(
              ([categoryName, options], index) => (
                <div key={categoryName} className="overflow-x-auto">
                  <div className="flex border-b border-gray-200 py-3">
                    <div className="flex-1 text-left text-sm font-medium text-gray-900">
                      {categoryName}
                    </div>
                    <div className="text-text-secondary w-32 text-center text-sm">
                      Primary
                      {index === 0 && (
                        <div className="text-xs font-normal">
                          (max 3, {3 - (watchedPrimaryExpertise?.length || 0)}{" "}
                          left)
                        </div>
                      )}
                    </div>
                    <div className="text-text-secondary w-32 text-center text-sm">
                      Secondary
                      {index === 0 && (
                        <div className="text-xs font-normal">
                          (max 3, {3 - (watchedSecondaryExpertise?.length || 0)}{" "}
                          left)
                        </div>
                      )}
                    </div>
                  </div>
                  {options.map((opt) => (
                    <div
                      key={opt.id}
                      className="flex items-center border-b border-gray-100 hover:bg-gray-50"
                    >
                      <div className="flex-1 py-3 pl-1 text-sm">{opt.name}</div>
                      <div className="flex w-32 justify-center py-2">
                        <div className="-my-2">
                          <FormSingleCheckboxField
                            control={control}
                            name="primaryExpertise"
                            value={opt.id.toString()}
                            maxCount={3}
                          />
                        </div>
                      </div>
                      <div className="flex w-32 justify-center py-2">
                        <div className="-my-2">
                          <FormSingleCheckboxField
                            control={control}
                            name="secondaryExpertise"
                            value={opt.id.toString()}
                            maxCount={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ),
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching format & delivery</CardTitle>
            <CardDescription>
              How do you deliver your coaching services?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormCheckboxGroupField
              control={control}
              name="coachingFormats"
              label="What coaching format do you offer?"
              options={[
                { value: "1-on-1", label: "1:1 Coaching" },
                { value: "group", label: "Group Coaching" },
              ]}
            />
            <FormCheckboxGroupField
              control={control}
              name="sessionDeliveryMethods"
              label="How do you deliver your coaching sessions?"
              options={[
                { value: "online", label: "Online (Zoom, Google Meet, etc.)" },
                { value: "in-person", label: "In-person" },
                { value: "hybrid", label: "Hybrid" },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Experience level</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormRadioGroupField
              control={control}
              name="experienceLevel"
              options={[
                {
                  value: "Rising Coach",
                  label: "Rising Coach (Goal-Driven Starter Package)",
                },
                {
                  value: "Experienced Coach",
                  label: "Experienced Coach (Proven Results Package)",
                },
                {
                  value: "Master Coach",
                  label: "Master Coach (Elite Transformation Partnership)",
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coaching style & approach</CardTitle>
            <CardDescription>Select up to 2</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormAdvancedCheckboxGroupField
              control={control}
              name="coachingApproaches"
              options={[
                {
                  value: "Exploratory & Self-Reflective",
                  label: "Self-discovery & deep reflection",
                },
                {
                  value: "Balanced guidance & structured advice",
                  label: "Balanced guidance & structured advice",
                },
                {
                  value: "Goal-oriented & action-driven",
                  label: "Goal-oriented & action-driven",
                },
                {
                  value: "Supportive & Encouraging",
                  label: "Supportive & motivational coaching",
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Faith & spirituality in coaching</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            <FormAdvancedCheckboxGroupField
              control={control}
              name="faithIntegration"
              options={[
                { value: "Christianity", label: "Christianity" },
                { value: "Islam", label: "Islam" },
                { value: "Judaism", label: "Judaism" },
                { value: "Hinduism", label: "Hinduism" },
                { value: "Buddhism", label: "Buddhism" },
                {
                  value: "Spiritual but not Religious",
                  label: "Spiritual but not Religious",
                },
                {
                  value: "I do not integrate spirituality into my coaching",
                  label: "I do not integrate spirituality into my coaching",
                },
              ]}
              onValueChange={(values, changedValue) => {
                const newValues = handleFaithIntegrationChange(
                  values,
                  changedValue,
                );
                setValue("faithIntegration", newValues);
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Personality profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormSelectField
              control={control}
              name="discType"
              label="DISC personality type"
              options={[
                "D",
                "I",
                "S",
                "C",
                "DI",
                "DC",
                "DS",
                "ID",
                "IC",
                "IS",
                "SD",
                "SI",
                "SC",
                "CD",
                "CI",
                "CS",
              ]}
              placeholder="Select DISC type"
            />
            <FormSelectField
              control={control}
              name="mbtiType"
              label="Myers-Briggs (MBTI) type"
              options={[
                "INTJ",
                "INTP",
                "ENTJ",
                "ENTP",
                "INFJ",
                "INFP",
                "ENFJ",
                "ENFP",
                "ISTJ",
                "ISFJ",
                "ESTJ",
                "ESFJ",
                "ISTP",
                "ISFP",
                "ESTP",
                "ESFP",
              ]}
              placeholder="Select MBTI type"
            />
            <FormSelectField
              control={control}
              name="enneagramType"
              label="Enneagram type"
              options={[
                "Type 1w9",
                "Type 1w2",
                "Type 2w1",
                "Type 2w3",
                "Type 3w2",
                "Type 3w4",
                "Type 4w3",
                "Type 4w5",
                "Type 5w4",
                "Type 5w6",
                "Type 6w5",
                "Type 6w7",
                "Type 7w6",
                "Type 7w8",
                "Type 8w7",
                "Type 8w9",
                "Type 9w8",
                "Type 9w1",
              ]}
              placeholder="Select Enneagram type"
            />
            <FormSelectField
              control={control}
              name="humanDesignType"
              label="Human Design type"
              options={[
                "Manifestor",
                "Generator",
                "Manifesting Generator",
                "Projector",
                "Reflector",
              ]}
              placeholder="Select Human Design type"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profile visibility</CardTitle>
          </CardHeader>
          <CardContent>
            <FormCheckboxField control={control} name="isPublic">
              Make my profile public
            </FormCheckboxField>
          </CardContent>
        </Card>
      </form>
    </>
  );
}
